// Brand-toned actionable chip used by the table's Command and Tasks cells.
//
// Both cells are buttons that look like data, so they share one component: the styling
// only has to be right, and right in both colour modes, in one place.
//
// Colours come from the theme's brand-tinted semantic tokens (client/theme/index.ts) —
// interactive-brand-hover and border-brand-light both carry a _dark pair, so nothing here
// hard-codes a light-mode-only value.

import { HStack, Icon, Text, Tooltip, useColorModeValue } from '@chakra-ui/react';
import { IconType } from 'react-icons';

interface TableChipProps {
  label: string;
  icon: IconType;
  tooltip: string;
  ariaLabel: string;
  testId: string;
  onClick: () => void;
  /** Monospace for command strings; the default reads as a normal label. */
  isMono?: boolean;
}

export function TableChip({
  label,
  icon,
  tooltip,
  ariaLabel,
  testId,
  onClick,
  isMono = false,
}: TableChipProps) {
  const labelColor = useColorModeValue('brand.700', 'brand.100');
  const hoverBg = useColorModeValue('brand.100', 'brand.800');

  return (
    <Tooltip label={tooltip} placement="top" openDelay={300}>
      <HStack
        as="button"
        type="button"
        aria-label={ariaLabel}
        data-testid={testId}
        onClick={onClick}
        spacing={1.5}
        px={2}
        py={0.5}
        borderRadius="md"
        bg="interactive-brand-hover"
        borderWidth="1px"
        borderColor="border-brand-light"
        color={labelColor}
        _hover={{ bg: hoverBg, borderColor: 'brand.400' }}
        transition="all 0.15s"
      >
        <Text
          fontFamily={isMono ? 'mono' : undefined}
          fontSize={isMono ? 'xs' : 'sm'}
          fontWeight="medium"
          whiteSpace="nowrap"
        >
          {label}
        </Text>
        {/* currentColor so the icon always sits on the chip's own contrast pair. */}
        <Icon as={icon} boxSize={3} color="currentColor" />
      </HStack>
    </Tooltip>
  );
}
